import React from 'react';
import {productTypeLabels} from 'mayhem-taxonomy';
import {DELIVERABLE_PRODUCT_TYPES} from '../../../../../shared/digitalFulfillment.js';
import Alert from '../../Alert';
import {isBrowserPreviewableImage, mediaKind} from '../Media/MediaCard';
import {
    ALL_MARKET_COUNTRIES,
    formatMarketAmount,
    formatMarketCountries,
    normalizeMarketCountries,
} from '../../../helpers/marketSettings';

const SHIPPED_TYPES = ['physical', 'commission'];

const FILE_ICON_CLASSES = {
    image: 'fa-file-image',
    video: 'fa-file-video',
    audio: 'fa-file-audio',
    pdf: 'fa-file-pdf',
    file: 'fa-file',
};

function fileName(file) {
    return file?.title || file?.filename || `File ${file?.id ?? ''}`.trim();
}

function filePreviewUrl(file) {
    if (!file?.cdnUrl) return '';
    return `${file.cdnUrl}?w=160&h=120&etag=${file.etag || file.uuid || ''}`;
}

function formatBytes(size) {
    const bytes = Number(size);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

function toIdString(value) {
    return value == null ? null : String(value);
}

/**
 * Resolve the delivery-file picture for a digital product from the publish-pane
 * payload: the primary file, any additional bundled files, and per-variant
 * override files. Also flags files that double as the listing image or a gallery
 * item (which would leak the paid deliverable to anyone browsing the storefront).
 */
function describeDelivery(product) {
    const type = product?.type;
    if (!DELIVERABLE_PRODUCT_TYPES.includes(type)) return null;

    const primaryFile = product?.file || null;
    const additionalFiles = Array.isArray(product?.additionalFiles)
        ? product.additionalFiles.filter(Boolean)
        : [];
    const variants = product?.variants && typeof product.variants === 'object'
        ? Object.values(product.variants)
        : [];
    const variantsWithFile = variants.filter(variant => variant?.fileId);
    const variantsMissingFile = variants.filter(variant => !variant?.fileId);

    // Files we can render with a name + preview (resolved server-side).
    const seen = new Set();
    const shownFiles = [];
    for (const [file, role] of [
        ...(primaryFile ? [[primaryFile, 'Primary']] : []),
        ...additionalFiles.map(file => [file, 'Additional']),
    ]) {
        const key = toIdString(file.id);
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        shownFiles.push({...file, role});
    }

    const mainImageId = toIdString(product?.imageId ?? product?.image?.id);
    const galleryFileIds = new Set(
        (Array.isArray(product?.media) ? product.media : [])
            .map(item => toIdString(item?.fileId ?? item?.file?.id))
            .filter(Boolean),
    );

    // Every id that is delivered to a paying customer, including variant
    // overrides we can only see by id.
    const deliveredRefs = [
        ...shownFiles.map(file => ({id: toIdString(file.id), label: fileName(file)})),
        ...variantsWithFile.map(variant => ({
            id: toIdString(variant.fileId),
            label: `${variant.name || 'Variant'} file`,
        })),
    ].filter(ref => ref.id);

    const imageClashes = dedupeLabels(
        deliveredRefs.filter(ref => mainImageId && ref.id === mainImageId),
    );
    const galleryClashes = dedupeLabels(
        deliveredRefs.filter(ref => galleryFileIds.has(ref.id)),
    );

    const hasAnyDeliveryFile = Boolean(primaryFile || product?.fileId)
        || variantsWithFile.length > 0;
    const everyVariantCovered = variants.length > 0 && variantsMissingFile.length === 0;

    const warnings = [];
    if (!hasAnyDeliveryFile) {
        warnings.push(
            'This digital product has no delivery file attached. Shoppers would have '
            + 'nothing to download after paying.',
        );
    } else if (product?.fileId && !primaryFile) {
        warnings.push(
            'The primary delivery file is missing or was deleted. Re-attach it in the '
            + 'Media step before publishing.',
        );
    }
    if (Number(product?.missingAdditionalFileCount) > 0) {
        const n = Number(product.missingAdditionalFileCount);
        warnings.push(
            `${n} additional delivery ${n === 1 ? 'file is' : 'files are'} missing or were `
            + 'deleted. Remove or replace them in the Media step.',
        );
    }
    if (!product?.fileId && !everyVariantCovered && variantsMissingFile.length > 0) {
        const n = variantsMissingFile.length;
        warnings.push(
            `${n} ${n === 1 ? 'variant has' : 'variants have'} no delivery file and there `
            + 'is no product-level file to fall back on.',
        );
    }
    if (imageClashes.length) {
        warnings.push(
            `${joinLabels(imageClashes)} ${imageClashes.length === 1 ? 'is' : 'are'} used as `
            + 'both a delivery file and the main product image, so anyone can download the '
            + 'paid file for free. Use a separate file for delivery.',
        );
    }
    if (galleryClashes.length) {
        warnings.push(
            `${joinLabels(galleryClashes)} ${galleryClashes.length === 1 ? 'is' : 'are'} in `
            + 'both the delivery files and the public gallery, so anyone can download the '
            + 'paid file for free. Remove it from the gallery.',
        );
    }

    return {
        shownFiles,
        variantFileCount: variantsWithFile.length,
        warnings,
    };
}

function dedupeLabels(refs) {
    const byLabel = new Map();
    for (const ref of refs) byLabel.set(ref.label, ref);
    return [...byLabel.values()];
}

function joinLabels(refs) {
    return refs.map(ref => `“${ref.label}”`).join(', ');
}

function listCountries(countries, emptyLabel = 'No supported countries') {
    return countries.length ? formatMarketCountries(countries) : emptyLabel;
}

function countriesMatch(left, right) {
    const normalizedLeft = normalizeMarketCountries(left, []);
    const normalizedRight = normalizeMarketCountries(right, []);
    return normalizedLeft.length === normalizedRight.length
        && normalizedLeft.every(country => normalizedRight.includes(country));
}

function getStorefrontCountries(channel) {
    return normalizeMarketCountries(
        channel?.shipToCountries,
        [String(channel?.countryCode || 'US').toUpperCase()],
    );
}

function getAvailabilityDisplay(product, channel) {
    if (Array.isArray(product?.availableCountries)) {
        const isEverywhere = product.availableCountries.some(country => (
            String(country).trim().toUpperCase() === ALL_MARKET_COUNTRIES
        ));
        if (isEverywhere && Array.isArray(product?.restrictedCountries)) {
            return product.restrictedCountries.length
                ? `Every supported country except: ${formatMarketCountries(
                    product.restrictedCountries,
                )}`
                : 'Every supported country';
        }
        if (isEverywhere) return 'Every supported country';
        return `Only: ${listCountries(product.availableCountries)}`;
    }
    if (Array.isArray(product?.restrictedCountries)) {
        return product.restrictedCountries.length
            ? `Storefront country except: ${formatMarketCountries(
                product.restrictedCountries,
            )}`
            : `Storefront country only: ${formatMarketCountries([
                String(channel?.countryCode || 'US').toUpperCase(),
            ])}`;
    }
    return `Storefront country only: ${formatMarketCountries([
        String(channel?.countryCode || 'US').toUpperCase(),
    ])}`;
}

function getShipToDisplay(product, channel) {
    const storefrontCountries = getStorefrontCountries(channel);
    if (Array.isArray(product?.shipToCountries)) {
        if (countriesMatch(product.shipToCountries, storefrontCountries)) {
            return `Storefront default: ${listCountries(storefrontCountries)}`;
        }
        return listCountries(product.shipToCountries);
    }
    return `Storefront default: ${listCountries(storefrontCountries)}`;
}

export default function ProductWizardPublish({
    product,
    channel,
    onPublish,
    onSaveDraft,
    onSetMarketplace,
    onBack,
    onDismiss,
    showListingControls = false,
    saving,
    error,
}) {
    const currency = product?.currency || channel?.defaultCurrency || 'USD';
    const priceDisplay = product?.price
        ? formatMarketAmount(product.price, currency)
        : 'Free';
    const typeDisplay = productTypeLabels[product?.type] || 'Unknown';
    const isLive = product?.isAvailable;
    const delivery = describeDelivery(product);

    return (
        <div className="wizard-step">
            <div className="wizard-publish-summary">
                <div className="wizard-publish-summary__section wizard-publish-summary__header">
                    <h3>Product Summary</h3>
                </div>

                {product?.image?.cdnUrl && (
                    <div className="wizard-publish-summary__section">
                        <img
                            src={product.image.cdnUrl}
                            alt={product.name}
                            className="wizard-publish-summary__image"
                        />
                    </div>
                )}

                <table className="wizard-publish-summary__table">
                    <tbody>
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Name</strong>
                            </td>
                            <td data-label="Name">{product?.name}</td>
                        </tr>
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Type</strong>
                            </td>
                            <td data-label="Type">{typeDisplay}</td>
                        </tr>
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Price</strong>
                            </td>
                            <td data-label="Price">{priceDisplay}</td>
                        </tr>
                        {delivery && (
                            <tr>
                                <td className="hide-mobile wizard-publish-summary__label">
                                    <strong>
                                        {delivery.shownFiles.length === 1
                                            ? 'Delivery File'
                                            : 'Delivery Files'}
                                    </strong>
                                </td>
                                <td data-label="Delivery Files">
                                    {delivery.shownFiles.length > 0 ? (
                                        <ul className="wizard-publish-summary__files">
                                            {delivery.shownFiles.map(file => {
                                                const kind = mediaKind(file);
                                                const preview = filePreviewUrl(file);
                                                const detail = [
                                                    file.role,
                                                    (file.extension || '').toUpperCase()
                                                        || file.mimetype,
                                                    formatBytes(file.size),
                                                ].filter(Boolean).join(' · ');
                                                return (
                                                    <li
                                                        key={`${file.role}-${file.id}`}
                                                        className="wizard-publish-summary__file"
                                                    >
                                                        <span className="wizard-publish-summary__file-preview">
                                                            {kind === 'image'
                                                                && isBrowserPreviewableImage(file)
                                                                && preview ? (
                                                                    <img src={preview} alt="" />
                                                                ) : (
                                                                    <i
                                                                        className={`far ${FILE_ICON_CLASSES[kind]}`}
                                                                        aria-hidden="true"
                                                                    ></i>
                                                                )}
                                                        </span>
                                                        <span className="wizard-publish-summary__file-meta">
                                                            <strong>{fileName(file)}</strong>
                                                            {detail && <span>{detail}</span>}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                            {delivery.variantFileCount > 0 && (
                                                <li className="wizard-publish-summary__file-note">
                                                    + {delivery.variantFileCount} variant-specific
                                                    {' '}
                                                    {delivery.variantFileCount === 1
                                                        ? 'file'
                                                        : 'files'}
                                                </li>
                                            )}
                                        </ul>
                                    ) : delivery.variantFileCount > 0 ? (
                                        <span>
                                            {delivery.variantFileCount} variant-specific
                                            {' '}
                                            {delivery.variantFileCount === 1 ? 'file' : 'files'}
                                        </span>
                                    ) : (
                                        <span className="error">No delivery file attached</span>
                                    )}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Origin</strong>
                            </td>
                            <td data-label="Origin">
                                {formatMarketCountries([
                                    String(channel?.countryCode || 'US').toUpperCase(),
                                ])}
                            </td>
                        </tr>
                        {(product?.doesRequireShipping
                            || SHIPPED_TYPES.includes(product?.type)) && (
                            <tr>
                                <td className="hide-mobile wizard-publish-summary__label">
                                    <strong>Ships To</strong>
                                </td>
                                <td data-label="Ships To">
                                    {getShipToDisplay(product, channel)}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Availability</strong>
                            </td>
                            <td data-label="Availability">
                                {getAvailabilityDisplay(product, channel)}
                            </td>
                        </tr>
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Marketplace</strong>
                            </td>
                            <td data-label="Marketplace">
                                {product?.inMarketplace ? 'Listed' : 'Not listed'}
                            </td>
                        </tr>
                        {product?.category && (
                            <tr>
                                <td className="hide-mobile wizard-publish-summary__label">
                                    <strong>Category</strong>
                                </td>
                                <td data-label="Category">{product.category}</td>
                            </tr>
                        )}
                        {product?.description && (
                            <tr>
                                <td className="hide-mobile wizard-publish-summary__label">
                                    <strong>Description</strong>
                                </td>
                                <td data-label="Description">{product.description}</td>
                            </tr>
                        )}
                        <tr>
                            <td className="hide-mobile wizard-publish-summary__label">
                                <strong>Listing</strong>
                            </td>
                            <td data-label="Status">
                                {isLive
                                    ? <span className="success">For sale</span>
                                    : <span className="text-muted">Draft — not for sale</span>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {delivery?.warnings.length > 0 && (
                <div className="wizard-publish-warnings">
                    {delivery.warnings.map(message => (
                        <Alert key={message} type="warning">{message}</Alert>
                    ))}
                </div>
            )}

            {showListingControls && (
                <div className="wizard-publish-listing-controls">
                    <h3>Listing Settings</h3>
                    <div className="group">
                        <label className="checkbox" htmlFor="publish-is-available">
                            <input
                                id="publish-is-available"
                                type="checkbox"
                                checked={Boolean(product?.isAvailable)}
                                disabled={saving}
                                onChange={event => (
                                    event.target.checked ? onPublish() : onSaveDraft()
                                )}
                            />
                            <span>For Sale</span>
                        </label>
                        <p className="help">
                            Show this product for sale on your storefront.
                        </p>
                    </div>
                    <div className="group">
                        <label className="checkbox" htmlFor="publish-in-marketplace">
                            <input
                                id="publish-in-marketplace"
                                type="checkbox"
                                checked={Boolean(product?.inMarketplace)}
                                disabled={saving}
                                onChange={event => onSetMarketplace(event.target.checked)}
                            />
                            <span>In Marketplace</span>
                        </label>
                        <p className="help">
                            List this product in the Mayhem Marketplace.
                        </p>
                    </div>
                </div>
            )}

            {error && <Alert type="error">{error}</Alert>}

            {isLive ? (
                <>
                    <p>Your product is currently <strong>for sale</strong>. You can take it down at any time by clicking <strong>Take Down Listing</strong> — it will become a draft and disappear from your store immediately. You can re-publish it whenever you're ready.</p>
                    <div className="wizard-nav">
                        <button type="button" className="button" onClick={onBack} disabled={saving}>
                            ← Back
                        </button>
                        <button type="button" className="button secondary" onClick={onSaveDraft} disabled={saving}>
                            {saving ? 'Saving…' : 'Take Down Listing'}
                        </button>
                        <button type="button" className="button primary" onClick={onDismiss} disabled={saving}>
                            Keep Published
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p>Your product is currently a <strong>draft</strong> and is not visible in your store. Click <strong>Publish Now</strong> to make it available for sale, or save it as a draft to publish later.</p>
                    <div className="wizard-nav">
                        <button type="button" className="button" onClick={onBack} disabled={saving}>
                            ← Back
                        </button>
                        <button type="button" className="button secondary" onClick={onSaveDraft} disabled={saving}>
                            {saving ? 'Saving…' : 'Save as Draft'}
                        </button>
                        <button type="button" className="button primary" onClick={onPublish} disabled={saving}>
                            {saving ? 'Publishing…' : 'Publish Now'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
